/**
 * Format number for display with thousands separator and decimal places
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted number string (e.g., "1,000,000.00")
 */
export const formatNumberForDisplay = (value, decimals = 2) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numValue =
    typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;

  if (isNaN(numValue)) {
    return "";
  }

  return numValue.toLocaleString("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Drop the padding zeros a DECIMAL column sends back (a tax rate stored as
 * decimal(8,4) arrives as "5.0000") without rounding the digits that matter
 * @param {number|string} value - e.g. "5.0000", "12.5000", "0.0750"
 * @returns {string|number|null|undefined} - e.g. "5", "12.5", "0.075"; anything
 *   that is not a plain decimal number is returned untouched
 */
export const trimTrailingZeros = (value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  const text = String(value).trim();
  if (!/^-?\d+\.\d+$/.test(text)) {
    return value;
  }

  return text.replace(/0+$/, "").replace(/\.$/, "");
};

/**
 * Parse formatted number string back to numeric value for backend
 * @param {string} formattedValue - The formatted string (e.g., "1,000,000.00")
 * @returns {number} - Numeric value (e.g., 1000000)
 */
export const parseFormattedNumber = (formattedValue) => {
  if (
    formattedValue === null ||
    formattedValue === undefined ||
    formattedValue === ""
  ) {
    return null;
  }

  const cleanedValue = String(formattedValue).replace(/,/g, "");
  const numValue = parseFloat(cleanedValue);

  return isNaN(numValue) ? null : numValue;
};

/**
 * Format number input on change event
 * @param {string} value - Raw input value
 * @param {number} decimals - Number of decimal places
 * @returns {object} - Object with display and raw values
 */
export const formatNumberInput = (value, decimals = 2) => {
  if (value === null || value === undefined || value === "") {
    return { display: "", raw: null };
  }

  // Remove all non-numeric characters except decimal point
  const cleaned = String(value).replace(/[^\d.]/g, "");

  // Handle multiple decimal points
  const parts = cleaned.split(".");
  const integerPart = parts[0] || "";
  const decimalPart = parts[1] || "";

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Construct display value. Truncate the decimal part to the allowed length, but
  // don't pad it with zeros here — this runs on every keystroke, and padding
  // immediately (e.g. "10." -> "10.00") jumps the caret to the end, so whatever
  // digit the user types next lands after the padded zeros instead of replacing
  // them. Zero-padding to a fixed width happens on blur instead (see AmountInput).
  let display = formattedInteger;
  if (parts.length > 1) {
    display += "." + decimalPart.slice(0, decimals);
  }

  // Parse the truncated value so extra keystrokes past `decimals`
  // never reach the form (Laravel `decimal:0,2` would reject them).
  const numericSource =
    parts.length > 1
      ? `${integerPart}.${decimalPart.slice(0, decimals)}`
      : integerPart;
  const parsed = parseFloat(numericSource);
  const raw = Number.isNaN(parsed) ? null : parsed;

  return { display, raw };
};

/**
 * Thousands-separator `formatter`/`parser` pair for AntD `InputNumber`'s own
 * props (e.g. an invoice line's `unit_price` or `discount_value`) — insert
 * commas into the integer part while typing, without touching decimals.
 * Not for `AmountInput`: it doesn't read `formatter`/`parser` at all, since it
 * builds its own display string via `formatNumberInput` above.
 * @param {number|string} value
 * @returns {string}
 */
export const commaFormatter = (value) =>
  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

/**
 * @param {string} value
 * @returns {string}
 */
export const commaParser = (value) => value.replace(/,/g, "");

/**
 * Check if a field name/key represents an amount or quantity
 * @param {string} fieldName - The field name to check
 * @returns {boolean}
 */
export const isAmountField = (fieldName) => {
  const amountKeywords = [
    "amount",
    "price",
    "cost",
    "total",
    "subtotal",
    "tax",
    "discount",
    "payment",
    "balance",
    "due",
    "salary",
    "wage",
    "fee",
    "charge",
    "rate",
    "value",
    "sum",
    "quantity",
    "qty",
    "count",
    "rent",
  ];

  const lowerFieldName = String(fieldName).toLowerCase();
  return amountKeywords.some((keyword) => lowerFieldName.includes(keyword));
};

/**
 * Auto-format numbers in an object based on field names
 * @param {object} data - Object containing data to format
 * @param {number} decimals - Number of decimal places
 * @returns {object} - Object with formatted values
 */
export const autoFormatAmounts = (data, decimals = 2) => {
  if (!data || typeof data !== "object") {
    return data;
  }

  const formatted = { ...data };

  Object.keys(formatted).forEach((key) => {
    if (
      isAmountField(key) &&
      (typeof formatted[key] === "number" || typeof formatted[key] === "string")
    ) {
      formatted[key] = formatNumberForDisplay(formatted[key], decimals);
    }
  });

  return formatted;
};
